import { useEffect, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Send, Bot, User, Loader2, ExternalLink } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useAgentChat } from '@/hooks/useAgentChat';
import { useDippyStore } from '@/stores/dippy-store';
import api from '@/lib/api';
import { cn } from '@/lib/utils';

export default function DippyPanelChat({ dippyName }: { dippyName: string }) {
  const location = useLocation();
  const { activeConversationId, setActiveConversation, setExpression } = useDippyStore();
  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const { messages, loading, sending, thinking, sendMessage } = useAgentChat({
    conversationId: activeConversationId,
  });

  // Auto-create or load most recent conversation
  useEffect(() => {
    if (activeConversationId) return;
    let cancelled = false;
    async function init() {
      try {
        const res = await api.get<{ conversations: Array<{ id: string }> }>('/admin/agent/conversations');
        if (cancelled) return;
        if (res.conversations.length > 0) {
          setActiveConversation(res.conversations[0].id);
        } else {
          const created = await api.post<{ conversation: { id: string } }>('/admin/agent/conversations', {
            title: `${dippyName} Chat`,
          });
          if (!cancelled) setActiveConversation(created.conversation.id);
        }
      } catch {
        // ignore
      }
    }
    init();
    return () => { cancelled = true; };
  }, [activeConversationId]);

  // Scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  // Update expression while thinking
  useEffect(() => {
    setExpression(thinking ? 'thinking' : 'idle');
  }, [thinking]);

  const handleSend = async () => {
    if (!input.trim()) return;
    const text = input;
    setInput('');
    await sendMessage(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isOnChatPage = location.pathname.startsWith('/admin/agent/chat');

  if (isOnChatPage) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-center">
        <Bot className="w-10 h-10 text-stone-300 mb-2" />
        <p className="text-sm text-stone-500 mb-3">You're on the full chat page</p>
        <button
          onClick={() => useDippyStore.getState().closePanel()}
          className="text-xs text-green-700 hover:text-green-900 font-medium"
        >
          Close panel
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Open full chat link */}
      {activeConversationId && (
        <Link
          to={`/admin/agent/chat/${activeConversationId}`}
          className="flex items-center gap-1 px-3 py-1.5 text-[11px] text-green-700 hover:text-green-900 font-medium border-b border-stone-100"
        >
          <ExternalLink className="w-3 h-3" />
          Open full chat
        </Link>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {loading ? (
          <div className="text-center py-4 text-stone-400">
            <Loader2 className="w-4 h-4 animate-spin mx-auto" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-6">
            <Bot className="w-8 h-8 text-stone-300 mx-auto mb-2" />
            <p className="text-xs text-stone-400">Ask {dippyName} anything</p>
          </div>
        ) : (
          messages.map((msg) => {
            if (msg.role === 'system') return null;
            const isUser = msg.role === 'user';
            return (
              <div key={msg.id} className={cn('flex gap-1.5', isUser ? 'flex-row-reverse' : 'flex-row')}>
                <div className={cn(
                  'w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5',
                  isUser ? 'bg-blue-600' : 'bg-stone-200'
                )}>
                  {isUser ? <User className="w-3 h-3 text-white" /> : <Bot className="w-3 h-3 text-stone-600" />}
                </div>
                <div className={cn(
                  'max-w-[85%] px-2.5 py-1.5 rounded-xl text-xs leading-relaxed',
                  isUser
                    ? 'bg-blue-600 text-white rounded-tr-sm'
                    : 'bg-stone-100 text-stone-800 rounded-tl-sm'
                )}>
                  {isUser ? msg.content : (
                    <div className="prose prose-xs prose-stone max-w-none [&>p]:mb-0.5">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}

        {thinking && (
          <div className="flex gap-1.5">
            <div className="w-5 h-5 rounded-full bg-stone-200 flex items-center justify-center shrink-0">
              <Bot className="w-3 h-3 text-stone-600" />
            </div>
            <div className="bg-stone-100 rounded-xl rounded-tl-sm px-2.5 py-1.5 text-xs text-stone-400 italic">
              Thinking...
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="p-2 border-t border-stone-100">
        <div className="flex gap-1.5 items-end">
          <textarea
            className="flex-1 resize-none rounded-lg border border-stone-200 px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-green-500 min-h-[32px] max-h-20"
            placeholder={`Ask ${dippyName}...`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={!activeConversationId || sending}
            rows={1}
          />
          <button
            onClick={() => handleSend()}
            disabled={!activeConversationId || !input.trim() || sending}
            className="p-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 shrink-0"
            aria-label="Send"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
